// Color tokens for the (chat-scoped) dark themes: 'dark' and the lower-contrast
// 'dim' (mid-grey surfaces, softer text, muted blue — like an IDE "dimmed" theme).
//
// Components that hardcode a color write it as `cv('<token>', '<original color>')`,
// which becomes `var(--cp-<token>, <original color>)`. The variables are only
// defined while dark mode is active (ChatThemeScope mounts them on :root), so in
// light mode every such value falls back to its original literal and renders
// exactly as before. Several near-identical light colors (e.g. the half-dozen
// light greys used for borders) intentionally share one dark token.
//
// Works in inline styles, sx, makeStyles and class components alike, since it is
// just a string. Not usable where MUI needs to parse the color (alpha(),
// darken(), palette entries) or in SVG presentation attributes (fill="..."):
// use style={{ fill: cv(...) }} for those.

export const cv = (token, fallback) => `var(--cp-${token}, ${fallback})`;

export const DARK_VARS = {
    // Surfaces
    'bg': '#15171b', // page background (matches theme background.default)
    'surface': '#1e2126', // white panels, cards, popovers
    'surface-alt': '#1a1c21', // the off-white (#FCFCFE) chat window / menu background
    'surface-muted': '#272a31', // #F5F5F5 headers, incoming bubbles, selected rows
    'surface-muted-hover': '#30343c',
    'surface-strong': '#3a3e47', // #D8D8D8 chips, hovered neutral chips
    'overlay': 'rgba(30, 33, 38, 0.8)', // translucent white scrims (loading, drop zone)
    'border': 'rgba(255, 255, 255, 0.12)',

    // Text
    'text': '#e7e9ed', // black / #202020 / #344054
    'text-secondary': '#b3b8c2', // #555 / #666 / #5F5F60
    'text-muted': '#8d939e', // #6c757d / #8A8A8F / #999
    'text-disabled': 'rgba(255, 255, 255, 0.3)', // rgba(0, 0, 0, 0.26)

    // Brand blue used as text or an icon color ('accent'), and as a fill behind
    // white text ('accent-fill': own bubble, send button, blue buttons/badges).
    'accent': '#4da3ff',
    'accent-hover': '#80bdff',
    'accent-tint': 'rgba(77, 163, 255, 0.16)', // #E3F2FD / #EAF4FF selected + info tints
    'accent-tint-strong': 'rgba(77, 163, 255, 0.28)', // #BBDEFB / #D0E7FF
    'accent-fill': '#007AFF',

    // Errors, the chatbot-handoff marker, excluded filter chips
    'danger-text': '#ff8a80',
    'danger-tint': 'rgba(255, 138, 128, 0.12)',
    'danger-tint-strong': 'rgba(255, 138, 128, 0.22)',
    'danger-border': 'rgba(255, 138, 128, 0.45)',

    // Pastel tag / chip backgrounds. Their text uses 'text'.
    'tag-tan': 'rgba(240, 190, 110, 0.2)', // #FDF3E1 tour tags
    'tag-lavender': 'rgba(148, 148, 206, 0.3)', // #E4E4F9 chat tags, Tour Guide
    'tag-pink': 'rgba(217, 140, 140, 0.3)', // #F8D7DA internal tags, Guest
    'tag-green': 'rgba(92, 184, 122, 0.24)', // #E1F0E6 PAX
    'tag-green-text': '#9fdcb4', // #184E31 PAX text
    'tag-teal': 'rgba(77, 208, 190, 0.24)', // #B7EBE3 Admin
    'tag-blue': 'rgba(100, 181, 246, 0.24)', // lightblue Observer, #DDEDFF sender tag
    'tag-indigo': 'rgba(92, 107, 192, 0.3)', // #E8EAF6 AI chips
    'tag-slate': 'rgba(150, 165, 190, 0.22)', // #E9EDF3 check-out chips

    // Amber notice cards (missed-tour rebooking request)
    'warn-tint': 'rgba(240, 195, 109, 0.1)', // #fffaf0 card background
    'warn-border': 'rgba(240, 195, 109, 0.45)', // #f0c36d
    'warn-text': '#f0c36d', // #8a5a00 headline

    // Sender-type message bubbles (chat/styles/messageTypeTokens.js)
    'bubble-operator': '#1f2c40', // #EFF4FC chatbot / automated / team
    'bubble-guide': '#29294a', // #F2F2FB tour guide
    'bubble-team-accent': '#6fa3ec', // #1C61BF team border
};

// Same tokens, pulled toward the middle: surfaces are lifted from near-black to
// muted greys, text drops from near-white to a soft grey, and the blues and tag
// tints are desaturated so nothing glows against the background.
export const DIM_VARS = {
    'bg': '#2b2e34',
    'surface': '#33363d',
    'surface-alt': '#2f3238',
    'surface-muted': '#3a3e46',
    'surface-muted-hover': '#42464f',
    'surface-strong': '#4b5059',
    'overlay': 'rgba(51, 54, 61, 0.8)',
    'border': 'rgba(255, 255, 255, 0.09)',

    'text': '#c5cad3',
    'text-secondary': '#a2a8b2',
    'text-muted': '#858b95',
    'text-disabled': 'rgba(255, 255, 255, 0.26)',

    'accent': '#6ea4e0',
    'accent-hover': '#90b9e8',
    'accent-tint': 'rgba(110, 164, 224, 0.14)',
    'accent-tint-strong': 'rgba(110, 164, 224, 0.24)',
    'accent-fill': '#3a72b8',

    'danger-text': '#e39a92',
    'danger-tint': 'rgba(227, 154, 146, 0.1)',
    'danger-tint-strong': 'rgba(227, 154, 146, 0.18)',
    'danger-border': 'rgba(227, 154, 146, 0.38)',

    'tag-tan': 'rgba(220, 185, 125, 0.18)',
    'tag-lavender': 'rgba(150, 150, 200, 0.24)',
    'tag-pink': 'rgba(205, 145, 145, 0.24)',
    'tag-green': 'rgba(110, 175, 130, 0.2)',
    'tag-green-text': '#a3cfb2',
    'tag-teal': 'rgba(100, 190, 178, 0.2)',
    'tag-blue': 'rgba(120, 170, 220, 0.2)',
    'tag-indigo': 'rgba(115, 125, 190, 0.26)',
    'tag-slate': 'rgba(150, 165, 190, 0.18)',

    'warn-tint': 'rgba(220, 185, 125, 0.08)',
    'warn-border': 'rgba(220, 185, 125, 0.36)',
    'warn-text': '#d8b77a',

    'bubble-operator': '#35404f',
    'bubble-guide': '#3b3b52',
    'bubble-team-accent': '#7ea6d8',
};

export const THEME_VARS = { dark: DARK_VARS, dim: DIM_VARS };

// :root declarations for GlobalStyles.
export const varDeclarations = (mode) =>
    Object.fromEntries(Object.entries(THEME_VARS[mode]).map(([k, v]) => [`--cp-${k}`, v]));
